import { formatDate } from './date';

type EmptyValue = null | undefined | '';

function isEmptyValue(value: unknown): value is EmptyValue {
  return value === null || value === undefined || value === '';
}

export function formatValue(
  value: string | number | null | undefined,
  defaultValue = '-',
): string {
  if (isEmptyValue(value)) {
    return defaultValue;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : defaultValue;
  }

  return String(value);
}

export function formatOptionalDate(
  value: Date | string | null | undefined,
  defaultValue = '-',
): string {
  if (!value) {
    return defaultValue;
  }

  if (value instanceof Date && Number.isNaN(value.getTime())) {
    return defaultValue;
  }

  return formatDate(value);
}

export function formatWithSuffix(
  value: string | number | null | undefined,
  suffix: string,
  defaultValue = '-',
): string {
  const formatted = formatValue(value, '');
  return formatted ? `${formatted} ${suffix}` : defaultValue;
}

export function formatMoney(
  value: number | null | undefined,
  defaultValue = '-',
): string {
  return formatWithSuffix(value, '€', defaultValue);
}

export function formatDays(
  value: number | string | null | undefined,
  daysLabel: string,
  defaultValue = '-',
): string {
  return formatWithSuffix(value, daysLabel, defaultValue);
}

export function formatPercentage(
  value: number | string | null | undefined,
  defaultValue = '-',
): string {
  const formatted = formatValue(value, '');
  return formatted ? `${formatted}%` : defaultValue;
}

export function formatStatus(
  status: string | { label?: string | null } | null | undefined,
  defaultValue = '-',
): string {
  if (!status) {
    return defaultValue;
  }

  if (typeof status === 'object') {
    return formatValue(status.label, defaultValue);
  }

  return formatValue(status, defaultValue);
}

export type CountryLike = {
  id: number | string;
  ctry_name?: string | null;
};

export function formatCountryLabel(
  value: CountryLike | string | number | null | undefined,
  countries: CountryLike[],
  defaultValue = '-',
): string {
  if (!value) {
    return defaultValue;
  }

  if (typeof value === 'object') {
    return formatValue(value.ctry_name, defaultValue);
  }

  const country = countries.find((item) => String(item.id) === String(value));
  return formatValue(country?.ctry_name, defaultValue);
}
